import { AiOutlineInfoCircle } from "react-icons/ai";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import { JurisdictionStatusReadable } from "../../types";

import { Anchor } from "~/components/Button";
import * as DetailList from "~/components/DetailList";
import { Box, Padding, Content } from "~/components/Elements";
import ExternalIcon from "~/components/icons/External";
import SkeletonDetailList from "~/components/SkeletonDetailList";
import Spacer, { Axis } from "~/components/Spacer";
import { VASP } from "~/types/vaspid";

const EMPTY = "-";

const VerticallyAlignedDiv = styled.div`
  display: flex;
  align-items: center;
  line-height: normal;
`;

const InfoIcon = styled(AiOutlineInfoCircle).attrs(() => ({
  fill: "#8E8E8E",
}))`
  font-size: 18px;
  margin-left: 4px;
  outline: none;
  cursor: pointer;
`;

const OpaqueTooltip = styled(ReactTooltip).attrs(() => ({
  place: "right",
  effect: "solid",
  border: true,
  borderColor: "lightgray",
  backgroundColor: "#FFF",
  textColor: "#000",
}))`
  opacity: 1 !important;
`;

const TooltipTextContainer = styled.div`
  max-width: 300px;
  font-weight: 500;
`;
const Container = styled.div`
  width: 100%;
`;

const SendsDataTransfers = ({ vasp }: { vasp: VASP }) => {
  const label = "Sends Transfers";
  const tooltipText = "This VASP has sent at least one Travel Rule data transfer";
  const value = vasp.isActiveSender ? "👍 Yes" : "🚫 No information";

  return (
    <DetailList.Item key={label} mobile={12} tablet={6} desktop={3}>
      <VerticallyAlignedDiv>
        <DetailList.Term>{label}</DetailList.Term>
        <InfoIcon data-tip data-for={label} />
        <OpaqueTooltip id={label}>
          <TooltipTextContainer>{tooltipText}</TooltipTextContainer>
        </OpaqueTooltip>
      </VerticallyAlignedDiv>
      <DetailList.Description className="localize-ignore">{value || EMPTY}</DetailList.Description>
    </DetailList.Item>
  );
};

const RepliesDataTransfers = ({ vasp }: { vasp: VASP }) => {
  const label = "Replies to Transfers";
  const tooltipText = "This VASP has accepted/declined an incoming Travel Rule data transfer";
  const value = vasp.isActiveReceiver ? "👍 Yes" : "🚫 No information";

  return (
    <DetailList.Item key={label} mobile={12} tablet={6} desktop={3}>
      <VerticallyAlignedDiv>
        <DetailList.Term>{label}</DetailList.Term>
        <InfoIcon data-tip data-for={label} />
        <OpaqueTooltip id={label}>
          <TooltipTextContainer>{tooltipText}</TooltipTextContainer>
        </OpaqueTooltip>
      </VerticallyAlignedDiv>
      <DetailList.Description className="localize-ignore">{value || EMPTY}</DetailList.Description>
    </DetailList.Item>
  );
};

const JurisdictionStatusSection = ({ vasp }: { vasp: VASP }) => {
  const label = "Jurisdiction Travel Rule Status";
  const value = JurisdictionStatusReadable[vasp.jurisdictionStatus || "notAvailable"];

  return (
    <DetailList.Item key={label} mobile={12} tablet={6} desktop={3}>
      <VerticallyAlignedDiv>
        <DetailList.Term>
          <div style={{ whiteSpace: "nowrap" }}>{label}</div>
        </DetailList.Term>
      </VerticallyAlignedDiv>
      <DetailList.Description className="localize-ignore">
        <div style={{ display: "flex" }}>
          {value}
          {vasp.jurisdictionNotabenePage && (
            <>
              <Spacer axis={Axis.HORIZONTAL} size={4} />
              <Anchor
                href={vasp.jurisdictionNotabenePage}
                target="_blank"
                rel="noreferrer"
                weight="normal"
                color="black"
                data-testid="jurisdictionPageLink"
              >
                <ExternalIcon />
              </Anchor>
            </>
          )}
        </div>
      </DetailList.Description>
    </DetailList.Item>
  );
};

export default function VASPImplementationStatus({ vasp }: { vasp?: VASP | null }) {
  return (
    <Box as={Container}>
      <Padding sized="medium">
        <DetailList.Header>
          <DetailList.Item mobile={12}>
            <DetailList.Title as="h2">Travel Rule Activity</DetailList.Title>
          </DetailList.Item>
        </DetailList.Header>
        <Content>
          The FATF Travel Rule is a new requirement for cryptocurrency exchanges, digital wallet providers, and
          financial institutions that exchange, hold, safe keep, convert and sell virtual assets. This rule requires
          virtual asset service providers (VASPs) to disclose specific customer data when transacting cryptoassets over
          a particular threshold.
        </Content>
        {vasp ? (
          <DetailList.Content>
            {<SendsDataTransfers vasp={vasp} />}
            {<RepliesDataTransfers vasp={vasp} />}
            {<JurisdictionStatusSection vasp={vasp} />}
          </DetailList.Content>
        ) : (
          <SkeletonDetailList count={8} />
        )}
      </Padding>
    </Box>
  );
}
